import { useCallback, useEffect, useRef, useState } from "react";

const getHash = () => window.location.hash.replace(/^#/, "");

const isMobileViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches;

const CASCADE_STEP = 28;

export function useWindows(registry) {
  const [windows, setWindows] = useState([]);
  const zCounter = useRef(10);
  const cascadeCounter = useRef(0);
  const [isMobile, setIsMobile] = useState(isMobileViewport);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 820px)");
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const nextZ = useCallback(() => {
    zCounter.current += 1;
    return zCounter.current;
  }, []);

  const focusWindow = useCallback(
    (id) => {
      setWindows((current) =>
        current.map((w) => (w.id === id ? { ...w, z: nextZ(), minimized: false } : w))
      );
    },
    [nextZ]
  );

  const openWindow = useCallback(
    (id, overrides = {}) => {
      setWindows((current) => {
        const existing = current.find((w) => w.id === id);
        if (existing) {
          return current.map((w) =>
            w.id === id ? { ...w, minimized: false, z: nextZ() } : w
          );
        }
        const def = registry[id];
        if (!def) return current;

        const mobile = isMobileViewport();
        const cascade = cascadeCounter.current % 8;
        cascadeCounter.current += 1;

        const width = overrides.width || def.width || 560;
        const height = overrides.height || def.height || 420;

        const win = {
          id,
          title: overrides.title || def.title,
          icon: overrides.icon || def.icon,
          width,
          height,
          x: mobile ? 0 : 90 + cascade * CASCADE_STEP,
          y: mobile ? 0 : 64 + cascade * CASCADE_STEP,
          z: nextZ(),
          minimized: false,
          maximized: mobile || !!def.startMaximized,
          closable: def.closable !== false,
          meta: overrides.meta || null,
        };
        return [...current, win];
      });
    },
    [registry, nextZ]
  );

  const closeWindow = useCallback((id) => {
    setWindows((current) => current.filter((w) => w.id !== id));
  }, []);

  const minimizeWindow = useCallback((id) => {
    setWindows((current) => current.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
  }, []);

  const toggleMaximize = useCallback((id) => {
    setWindows((current) =>
      current.map((w) => (w.id === id ? { ...w, maximized: !w.maximized } : w))
    );
  }, []);

  const moveWindow = useCallback((id, x, y) => {
    setWindows((current) => current.map((w) => (w.id === id ? { ...w, x, y } : w)));
  }, []);

  const restoreOrToggle = useCallback(
    (id) => {
      setWindows((current) => {
        const win = current.find((w) => w.id === id);
        if (!win) return current;
        const topZ = Math.max(...current.map((w) => w.z));
        if (win.minimized) {
          return current.map((w) => (w.id === id ? { ...w, minimized: false, z: nextZ() } : w));
        }
        if (win.z === topZ) {
          return current.map((w) => (w.id === id ? { ...w, minimized: true } : w));
        }
        return current.map((w) => (w.id === id ? { ...w, z: nextZ() } : w));
      });
    },
    [nextZ]
  );

  useEffect(() => {
    const applyHash = (hash) => {
      if (!hash || hash === "top") return;
      if (registry[hash]) {
        openWindow(hash);
      }
    };
    applyHash(getHash());

    const onHashChange = () => applyHash(getHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    windows,
    isMobile,
    openWindow,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    moveWindow,
    focusWindow,
    restoreOrToggle,
  };
}
